import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import get from 'lodash/get';
import { useRouter } from 'next/router';

import Link from '@g4/next-furniture/Link';
import tracking from '@g4/g4-analytics';
import { TrackClick } from '@g4/trackster';
import { useUserContext } from '@g4/next-furniture/UserContextProvider';

import { useMedia, Box, Text, VerticalSpacing } from '../../Boneyard';
import { useAuthentication } from '../../../graphql';
import { useTranslations } from '../../../graphql/components/ApplicationQueryProvider';
import NavGroup from './NavGroup';
import ExpandableMenuItem from './navGroup/ExpandableMenuItem';
import Markdown from '../../../components/Markdown';
import { HEADER_USER_MENU, HOME_LOGIN } from '../../../../automationHooks.json';
import { useModal } from '../../ModalProvider';
import LoginSignup, {
  LoginSignupTitle,
  LoginSignupHeaderLogo,
} from '../../LoginSignup';
import useUserMenuQuery from './useUserMenuQuery';
import { LOGOUT_PATH } from '../../../utils/authUtils';
import DEFAULT_AVATAR from '../../../images/ic-default-avatar.png';
import IsStadium from '../../../utils/isStadium';
import { useSSRQuery } from '../../../graphql';
import TRAVEL_AGENT_LOGIN_QUERY from '../../../pageComponents/TALoginPage/taLoginPageTranslations.query.graphql.js';
import { hasTargetingConsent } from '../../../utils/ga4Utils';

const UserMenu = ({ theme, isBurgerMenu }) => {
  const {
    isAuthenticated,
    logout,
    iataNumber,
    agentName,
  } = useAuthentication();

  const mq = useMedia();
  const translations = useTranslations();
  const router = useRouter();
  const { isTravelAgent } = useUserContext();

  const {
    setShowModal,
    setLogo,
    setTitle,
    setIsMobile,
    setContent,
    setAutomationHook,
  } = useModal();

  const { data } = useSSRQuery(TRAVEL_AGENT_LOGIN_QUERY, {
    skip: !isTravelAgent,
  });
  const travelAgentLoginPage =
    data?.application?.translations?.travelAgentLoginPage;

  const renderLoginSignupModal = useCallback(
    redirectOption => {
      setTitle(<LoginSignupTitle />);
      setLogo(<LoginSignupHeaderLogo isMobile={mq.lte('sm')} />);
      setContent(
        <LoginSignup
          isMobile={mq.lte('sm')}
          {...(redirectOption ? { redirectOption } : {})}
        />
      );
      setIsMobile(mq.lte('sm'));
      setShowModal(true);
      setAutomationHook(HOME_LOGIN.hook);
    },
    [
      setLogo,
      setTitle,
      setContent,
      setIsMobile,
      setShowModal,
      setAutomationHook,
      mq,
    ]
  );

  const openModal = e => {
    e.preventDefault();
    renderLoginSignupModal();
    tracking.trackEvent('site_click_tracking', 'Top Nav', 'Login', null);
  };

  const {
    loading,
    error,
    refetchUserMenu,
    id,
    firstName,
    lastName,
    loyalty,
    userMenuItems,
  } = useUserMenuQuery({
    skipSummary: !isAuthenticated,
  });

  const redirectToDestination = useCallback(() => {
    const redirectPath = router?.query?.destination;
    if (redirectPath && !window.location.pathname.includes('/updatepassword')) {
      router.push(redirectPath);
    }
  }, [router]);

  const refetchUserMenuData = useCallback(() => {
    refetchUserMenu && refetchUserMenu();
  }, [refetchUserMenu]);

  useEffect(() => {
    refetchUserMenuData();
  }, [isAuthenticated, refetchUserMenuData]);

  useEffect(() => {
    const showLoginSignupPopup =
      !isAuthenticated && window.location.hash === '#user-login';

    if (showLoginSignupPopup) {
      renderLoginSignupModal(redirectToDestination);
    }
  }, [isAuthenticated, renderLoginSignupModal, redirectToDestination]);

  // Returns login button for unauthenticated user
  if (!isAuthenticated && !isTravelAgent) {
    return (
      <NavGroup
        items={[
          {
            __typename: 'NavigationMenuItem',
            id: '7411',
            icon: 'ALLWAYS',
            title: 'Log in',
            link: null,
            onclick: openModal,
          },
        ]}
        automationHook={HEADER_USER_MENU.hook}
        displayAsColumn={mq.lt('md')}
        isAllegiantStadiumNavItems={IsStadium() ? true : false}
      />
    );
  }

  redirectToDestination();

  if (loading) {
    return null;
  }

  if (error) {
    throw error;
  }

  const Menu = styled(Box)`
    display: flex;
    width: ${mq.lte('sm') ? '100%' : '320px'};
    flex-direction: column;
    background-color: #ffffff;
    border: 1px solid #cfd8dc;
    padding: ${mq.lte('sm') ? '0px' : '15px 20px'};
    border-radius: 0 0 8px 8px;
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.4);
    color: ${theme.colors.common.peacockBlue};
  `;

  const MenuHeader = styled(Box)`
    display: flex;
    flex-direction: row;
    align-items: center;
    border: 1px inside #cfd8dc;
    position: relative;
    top: 5px;
  `;

  const Separator = styled.div`
    width: '100%';
    height: 2px;
    background-color: #f89728;
  `;

  const Avatar = styled.img.attrs({
    src: DEFAULT_AVATAR,
    alt: 'Avatar',
  })`
    width: 52px;
    height: 52px;
    border-radius: 50%;
    z-index: 1;
  `;

  const LinkText = styled.div`
    color: ${theme.colors.siteHeader.light.linkColor};
    padding: 3px;
    font-size: ${theme.toRem(16)};
    font-weight: 600;
    border-bottom: 3px solid transparent;
    transition: border-bottom 0.2s ease-in-out;

    &:hover {
      border-bottom: 3px solid ${theme.colors.siteHeader.linkActive};
    }
  `;

  const isActiveLoyaltyMember =
    loyalty && !['SUSPENDED', 'CANCELLED'].includes(loyalty.status);

  const isLogoutLink = link => link && link.path === LOGOUT_PATH;

  // Renders a nav item with icon and styles
  const NavItem = ({ item }) => {
    const { link, title } = item;
    const href = link.path || link.url;
    const linkTarget = `_${link.target.toLowerCase()}`;
    const logoutCallback = isLogoutLink(link) && {
      onClick: e => {
        if (window.location.pathname === '/updatepassword') {
          router.push(router.query.routeTo);
        } else if (
          window.location.search === `?password_updated=true` ||
          window.location.search === `?email_updated=true`
        ) {
          router.push(window.location.pathname);
        }
        e.preventDefault();
        logout && logout();
      },
    };
    const linkUrl = link.path
      ? window.location.href.slice(0, -1) + link.path
      : link.url;

    return (
      <Link href={href} target={linkTarget}>
        {({ anchorProps }) => (
          <Box
            as="a"
            d={'flex'}
            direction={'row'}
            alignItems="center"
            {...anchorProps}
            {...logoutCallback}
          >
            <TrackClick
              category="site_navigation_clicks"
              action={linkUrl}
              label={title}
            >
              <LinkText>
                <Text size={isLogoutLink(link) ? 14 : 'inherit'}>{title}</Text>
              </LinkText>
            </TrackClick>
          </Box>
        )}
      </Link>
    );
  };

  const renderPoints = loyalty => {
    if (!loyalty) {
      return null;
    }

    const { total, pointsValue } = loyalty;

    tracking.setDataLayerVars({
      loyalty_points_total: total,
      allways_account_no: isAuthenticated && hasTargetingConsent() ? id : '',
    });

    return (
      <Text weight={600} size={16}>
        <Markdown
          source={`{{totalPoints}} Points = {{pointsValue}}`}
          data={{
            totalPoints: (total > 0 ? total : 0).toLocaleString(),
            pointsValue: `$${(pointsValue > 0 ? pointsValue : 0).toFixed(2)}`,
          }}
        />
      </Text>
    );
  };

  const getAuthenticatedExpandableMenuItem = (
    loyalty,
    firstName,
    agentName
  ) => {
    const displayLoggedUser = isTravelAgent ? agentName : firstName;

    const headerGreeting = get(translations, 'usermenu.headerGreeting').replace(
      '{{firstName}}',
      displayLoggedUser
    );

    let description, icon;

    if (isTravelAgent) {
      description = {
        icon: 'ALLWAYS',
        details: travelAgentLoginPage?.taIataNumberDescription,
      };
    }

    if (loyalty && isActiveLoyaltyMember) {
      const { pointsValue } = loyalty;

      const headerPointsBalance = get(
        translations,
        'usermenu.headerPointsBalanceText'
      )
        .replace(
          '{{pointsValue}}',
          (pointsValue > 0 ? pointsValue : 0).toFixed(2)
        )
        .replace('#015699', IsStadium() ? 'white' : '#015699');

      description = {
        icon: 'ALLWAYS',
        details: headerPointsBalance,
      };
    } else {
      icon = 'ALLWAYS';
    }

    return {
      __typename: 'ExpandableMenuItem',
      id: 'my-allegiant',
      title: headerGreeting,
      trackLabel: 'Login',
      style: {
        fontSize: '14px',
        fontColor: '#263238',
      },
      link: null,
      expanded: mq.lt('md'),
      items: {},
      description,
      icon,
    };
  };

  const redirectToTALogin = async () => {
    //Log out will redirect to travel agent login page
    logout && (await logout());
    window.location.href = '/ta-login';
  };
  const menuItems = [...userMenuItems];

  const headerItem = menuItems.shift();
  const footerItem = menuItems.pop();

  if (isBurgerMenu) {
    return (
      <Box>
        <Avatar />
        <VerticalSpacing size={2} />
        <Box d={'flex'} direction={'column'}>
          <Text weight={500} size={16} color="#263238">
            {`${firstName} ${lastName.charAt(0).toUpperCase()}.`}
          </Text>
          <VerticalSpacing size={1} />
          {isActiveLoyaltyMember && renderPoints(loyalty)}
          <Text weight={500} size={12}>
            <Markdown
              source={`Allways™ #{{customerId}}`}
              data={{
                customerId: id,
              }}
            />
          </Text>
        </Box>
        <VerticalSpacing size={4} />
        <NavItem item={headerItem} />
        <VerticalSpacing size={4} />
        <Separator />
        <VerticalSpacing size={4} />
        <Box d={'flex'} direction={'column'}>
          {menuItems.map(item => (
            <div key={item.id}>
              <NavItem item={item} />
              <VerticalSpacing size={1} />
            </div>
          ))}
        </Box>
        <VerticalSpacing size={2} />
        <Separator />
        <VerticalSpacing size={4} />
        <NavItem item={footerItem} />
        <VerticalSpacing size={2} />
      </Box>
    );
  }

  return (
    <ExpandableMenuItem
      isAllegiantStadiumNavItems={IsStadium() ? true : false}
      item={getAuthenticatedExpandableMenuItem(loyalty, firstName, agentName)}
      isCustomMenu={true}
      isMultiLineItem={isActiveLoyaltyMember}
      alignRight={true}
    >
      <Menu>
        <MenuHeader>
          <Avatar />
          {!isTravelAgent && (
            <Box d={'flex'} direction={'column'} p={2}>
              <Text weight={500} size={16} color="#263238">
                {`${firstName} ${lastName.charAt(0).toUpperCase()}.`}
              </Text>
              <VerticalSpacing size={1} />
              {isActiveLoyaltyMember && renderPoints(loyalty)}
              <Text weight={500} size={12}>
                <Markdown
                  source={`Allways™ #{{customerId}}`}
                  data={{
                    customerId: id,
                  }}
                />
              </Text>
            </Box>
          )}
          {isTravelAgent && (
            <Box d={'flex'} direction={'column'} p={2}>
              <Text weight={500} size={16}>
                {`${agentName}.`}
              </Text>
              <VerticalSpacing size={1} />
              <Text weight={300} size={12}>
                <Markdown
                  source={travelAgentLoginPage?.taIataNumberLabel}
                  data={{ iataNumber: iataNumber }}
                />
              </Text>
            </Box>
          )}
        </MenuHeader>
        <VerticalSpacing size={4} />
        {!isTravelAgent ? (
          <>
            <NavItem item={headerItem} />
            <VerticalSpacing size={4} />
            <Separator />
            <VerticalSpacing size={4} />
            <Box d={'flex'} direction={'column'}>
              {menuItems.map(item => (
                <div key={item.id}>
                  <NavItem item={item} />
                  <VerticalSpacing size={1} />
                </div>
              ))}
            </Box>
            <VerticalSpacing size={2} />
            <Separator />
            <VerticalSpacing size={4} />
            <NavItem item={footerItem} />
          </>
        ) : (
          <>
            <VerticalSpacing size={4} />
            <Separator />
            <VerticalSpacing size={4} />
            <Box
              as="a"
              d={'flex'}
              direction={'row'}
              alignItems="center"
              onClick={redirectToTALogin}
            >
              <LinkText>{travelAgentLoginPage?.taLogOutLabel}</LinkText>
            </Box>
          </>
        )}
        <VerticalSpacing size={2} />
      </Menu>
    </ExpandableMenuItem>
  );
};

UserMenu.propTypes = {
  item: PropTypes.object,
  theme: PropTypes.object.isRequired,
  isBurgerMenu: PropTypes.bool,
};

export default withTheme(UserMenu);
